import { Badge, Group, Paper, Stack, Text } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { TitleCard } from '@/core/components';

import { formatDate, notEmptyArray } from '@feature/utils';

import { TravelRequestSummary, formatTravelRegistration } from '../../model/TravelRequestTypes';
import { TravelDayBalanceSection } from '../TravelDayBalanceSection';
import { TravelRegistryTypeBadge } from '../TravelRegistryTypeBadge';
import { TravelRequestStatusBadge } from '../TravelRequestStatusBadge';
import classes from './TravelRequestCard.module.css';

export interface TravelRequestCardProps {
  request: TravelRequestSummary;
  to: string;
  showOfficeStep?: boolean;
}

export function TravelRequestCard({ request, to, showOfficeStep = false }: TravelRequestCardProps) {
  const { t } = useTranslation();
  const {
    requester,
    name,
    activityType,
    city,
    country,
    departureDate,
    returnDate,
    status,
    currentStepKey,
    lateSubmission,
    parallelRequests,
    unfinishedReturns,
    registration,
    leaveDaysExceeded,
    dayCapExceeded,
    dayBalance,
  } = request;

  const activityTypeLabel = t(`travel.activityTypeEnum.${activityType}`);
  const place = [city, country?.name].filter(notEmptyArray).join(', ');
  // A rejection leaves the later steps pending, so the step counts only while the request still waits on it.
  const officeStepKey =
    showOfficeStep &&
    status === 'SUBMITTED' &&
    (currentStepKey === 'SECRETARIAT_VERIFICATION' || currentStepKey === 'ADMINISTRATIVE_BOARD')
      ? currentStepKey
      : null;

  return (
    <Paper
      className={classes.card}
      component={Link}
      to={to}
      p="lg"
      c="var(--mantine-color-text)"
    >
      <Stack gap="10px">
        <TitleCard
          title={name ?? activityTypeLabel}
          below={registration ? <TravelRegistryTypeBadge registryType={registration.registryType} /> : undefined}
        />
        <Text size="sm">{requester.name}</Text>
        <Group>
          <Text
            c="dimmed"
            size="sm"
          >
            {activityTypeLabel}
          </Text>
          <Text
            c="dimmed"
            size="sm"
          >
            {place === '' ? '-' : place}
          </Text>
          <Text
            c="dimmed"
            size="sm"
          >
            {formatDate(departureDate) ?? '-'} - {formatDate(returnDate) ?? '-'}
          </Text>
        </Group>
        <Group>
          <TravelRequestStatusBadge status={status} />
          {officeStepKey && (
            <Badge
              color="violet"
              variant="light"
            >
              {t(`travel.stepKeyEnum.${officeStepKey}`)}
            </Badge>
          )}
          {lateSubmission && <Badge color="red">{t('travel.requests.view.lateSubmission')}</Badge>}
          {parallelRequests > 0 && (
            <Badge
              color="blue"
              variant="light"
            >
              {t('travel.requests.view.parallelBadge', { count: parallelRequests })}
            </Badge>
          )}
          {unfinishedReturns > 0 && (
            <Badge color="red">{t('travel.requests.view.unfinishedReturnsBadge', { count: unfinishedReturns })}</Badge>
          )}
          <Text
            c="dimmed"
            size="sm"
          >
            {registration ? formatTravelRegistration(registration) : t('travel.requests.view.notRegistered')}
          </Text>
        </Group>
        <TravelDayBalanceSection
          activityType={activityType}
          leaveDaysExceeded={leaveDaysExceeded}
          dayCapExceeded={dayCapExceeded}
          dayBalance={dayBalance}
        />
      </Stack>
    </Paper>
  );
}
